
import { timeTextLabel } from '../utils/shiftTime';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, X, Check, CalendarDays, MessageCircle, ShieldCheck, User, Loader2, Mail, LogOut } from 'lucide-react';
import Modal from '../components/ui/Modal';
import { api, Staff, DefaultShiftSetting, isLineEnabled } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';

const DAYS = ['月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日', '日曜日'];
// 曜日名→dayOfWeek変換（月=1, 火=2, ... 日=0）
const DAY_MAP: Record<string, number> = { '月曜日': 1, '火曜日': 2, '水曜日': 3, '木曜日': 4, '金曜日': 5, '土曜日': 6, '日曜日': 0 };
type DefaultSettingType = 'off' | 'preset' | 'custom';
interface DaySetting { day: string; type: DefaultSettingType; presetId?: number; startTime?: string; endTime?: string; }
interface Preset { id: number; label: string; time: string; color: string; }

const UserDefaultSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser: authUser } = useAuth();
  const { userProfile: contextUserProfile, currentStore } = useUser();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [userProfile, setUserProfile] = useState<Staff | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [presets, setPresets] = useState<Preset[]>([]);

  const loadData = async () => {
    setIsLoading(true);
    try {
        const userId = authUser?.uid || contextUserProfile?.id || null;
        if (userId) {
            const staffList = await api.getStaff();
            const me = staffList.find(s => s.id === userId);
            setUserProfile(me || null);

            // Firestoreから保存済みデフォルト設定を読み込み
            const storeId = currentStore?.id || me?.stores?.[0];
            if (storeId) {
              const saved = await api.getDefaultSettings(userId, storeId);
              if (saved.length > 0) {
                const loaded: Record<string, DaySetting> = {};
                DAYS.forEach(day => { loaded[day] = { day, type: 'off' }; });
                saved.forEach(s => {
                  const dayName = DAYS.find((_, i) => DAY_MAP[DAYS[i]] === s.dayOfWeek);
                  if (dayName) {
                    loaded[dayName] = { day: dayName, type: s.type, presetId: s.presetId, startTime: s.startTime, endTime: s.endTime };
                  }
                });
                setSettings(loaded);
              }
            }
        }

        const types = await api.getShiftTypes();
        // 希望として選べるのは勤務の区分のみ（有給・欠勤などは除く）。
        const mappedPresets = types.filter(t => !t.category || t.category === 'work' || t.category === 'holiday_work').map((t, index) => {
            const colors = ['bg-blue-100 text-blue-700 border-blue-200', 'bg-teal-100 text-teal-700 border-teal-200', 'bg-orange-100 text-orange-700 border-orange-200', 'bg-purple-100 text-purple-700 border-purple-200', 'bg-pink-100 text-pink-700 border-pink-200'];
            return { id: t.id, label: t.name, time: t.time.replace('~', '-'), color: colors[index % colors.length] };
        });
        setPresets(mappedPresets);
    } catch (e) {
        console.error(e);
        setPresets([]);
    } finally {
        setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);
  
  const [settings, setSettings] = useState<Record<string, DaySetting>>(() => {
    const initial: Record<string, DaySetting> = {};
    DAYS.forEach(day => { initial[day] = { day, type: 'off' }; });
    return initial;
  });

  const [tempSetting, setTempSetting] = useState<DaySetting>({ day: '', type: 'off' });

  const handleDayClick = (day: string) => {
    setSelectedDay(day);
    setTempSetting({ ...settings[day] });
    setIsEditModalOpen(true);
  };

  const handleSave = async () => {
    if (selectedDay) { setSettings(prev => ({ ...prev, [selectedDay]: tempSetting })); }
    setIsEditModalOpen(false);

    // Firestoreに永続化
    const userId = authUser?.uid || contextUserProfile?.id;
    const storeId = currentStore?.id || userProfile?.stores?.[0];
    if (!userId || !storeId) return;

    setIsSaving(true);
    try {
      const allSettings = { ...settings };
      if (selectedDay) allSettings[selectedDay] = tempSetting;
      const payload: DefaultShiftSetting[] = DAYS.map(day => ({
        id: `default_${userId}_${storeId}_${DAY_MAP[day]}`,
        staffId: userId,
        storeId,
        dayOfWeek: DAY_MAP[day],
        type: allSettings[day].type,
        presetId: allSettings[day].presetId,
        startTime: allSettings[day].startTime,
        endTime: allSettings[day].endTime,
      }));
      await api.saveDefaultSettings(payload);
      showToast('設定を保存しました。', 'success');
    } catch (e) {
      console.error(e);
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const getSettingLabel = (setting: DaySetting) => {
    if (setting.type === 'off') return <span className="text-slate-400 text-sm">希望なし（休み）</span>;
    if (setting.type === 'preset') {
      const preset = presets.find(p => p.id === setting.presetId);
      return (
        <div className="flex items-center">
            <span className={`text-xs font-bold px-2 py-0.5 rounded mr-2 ${preset?.color || 'bg-gray-100'}`}>{preset?.label}</span>
            <span className="text-xs text-slate-500">{timeTextLabel(preset?.time)}</span>
        </div>
      );
    }
    if (setting.type === 'custom') {
      return <div className="flex items-center text-slate-700 font-bold text-sm"><Clock size={14} className="mr-1.5 text-slate-400" />{timeTextLabel(`${setting.startTime}-${setting.endTime}`)}</div>;
    }
    return null;
  };

  if (isLoading) {
      return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center flex-col p-8">
              <Loader2 className="animate-spin text-blue-600 mb-4" size={32} />
              <p className="text-slate-400 text-xs font-bold">情報を読み込み中...</p>
          </div>
      );
  }

  const isLineConnected = userProfile?.lineStatus === 'connected';
  const lineEnabled = isLineEnabled(currentStore);

  const handleStartLineLink = async () => {
    if (!userProfile) return;
    try {
      const url = await api.generateInviteToken(userProfile.id);
      const hash = url.split('#')[1];
      if (hash) navigate(hash);
    } catch (e) {
      showToast('連携の準備に失敗しました。', 'error');
    }
  };

  const handleLogout = async () => {
    await api.signOut();
    showToast('ログアウトしました。', 'success');
    navigate('/login');
  };

  return (
    <div className="animate-in bg-gray-50 min-h-screen pb-24">
         <div className="bg-white border-b border-gray-200 sticky top-0 z-10 px-4 py-4 flex items-center justify-between shadow-sm">
             <div className="flex items-center">
                 <button onClick={() => navigate('/user/shifts')} className="p-2 -ml-2 mr-2 text-slate-500 hover:bg-gray-100 rounded-full"><ChevronLeft size={24} /></button>
                 <h1 className="text-lg font-bold text-slate-800">設定・プロフィール</h1>
             </div>
             <button onClick={() => navigate('/user/shifts')} className="text-sm font-bold text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors">完了</button>
         </div>

         <div className="p-4 space-y-6">
             {/* プロフィール & LINE連携状況カード */}
             <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-6 flex items-center gap-4 border-b border-gray-100">
                    <div>
                        <h2 className="text-xl font-black text-slate-800">{userProfile?.lastName} {userProfile?.firstName}</h2>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                                {userProfile?.role === 'system_admin' ? '管理者' : userProfile?.role === 'store_manager' ? '店舗責任者' : 'スタッフ'}
                            </span>
                        </div>
                    </div>
                </div>
                {lineEnabled ? (
                <div className="p-4 bg-slate-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${isLineConnected ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-500'}`}>
                            {isLineConnected ? <ShieldCheck size={20} /> : <MessageCircle size={20} />}
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">LINE連携状況</p>
                            <p className={`text-sm font-bold ${isLineConnected ? 'text-green-600' : 'text-orange-500'}`}>
                                {isLineConnected ? '連携済み' : '未連携'}
                            </p>
                        </div>
                    </div>
                    {!isLineConnected && (
                        <button 
                            onClick={handleStartLineLink} 
                            className="text-[10px] font-black text-blue-600 border border-blue-200 bg-white px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-all active:scale-95 shadow-sm"
                        >
                            連携設定を行う
                        </button>
                    )}
                </div>
                ) : (
                <div className="p-4 bg-slate-50/50 flex items-center gap-3" data-testid="user-login-info">
                    <div className="p-2 rounded-xl bg-blue-100 text-blue-600"><Mail size={20} /></div>
                    <div className="min-w-0">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">ログインID・お知らせの受け取り</p>
                        <p className="text-sm font-bold text-slate-700 break-all">{userProfile?.email}</p>
                        <p className="text-[11px] text-slate-500">シフトのお知らせはメールとアプリ内で受け取ります。</p>
                    </div>
                </div>
                )}
             </div>

             <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start">
                 <div className="bg-blue-100 text-blue-600 p-1.5 rounded-full mr-3 mt-0.5"><CalendarDays size={16} /></div>
                 <div className="text-sm text-blue-800">
                     <p className="font-bold mb-1">曜日ごとの基本シフト</p>
                     <p className="text-xs opacity-80 leading-relaxed">ここで設定した内容は、シフト提出時に自動で反映されます。</p>
                 </div>
             </div>

             <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                 {DAYS.map((day, index) => (
                     <button key={day} onClick={() => handleDayClick(day)} className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${index !== DAYS.length - 1 ? 'border-b border-gray-100' : ''}`}>
                         <div className="flex items-center">
                             <span className={`font-bold text-sm w-16 text-left ${day === '日曜日' ? 'text-red-500' : day === '土曜日' ? 'text-blue-600' : 'text-slate-700'}`}>{day}</span>
                             <div className="ml-4">{getSettingLabel(settings[day])}</div>
                         </div>
                         <ChevronRight size={18} className="text-gray-300" />
                     </button>
                 ))}
             </div>
         </div>

      <div className="px-4">
          <button onClick={handleLogout} className="w-full py-3 bg-white border border-gray-200 rounded-xl text-sm font-bold text-slate-500 flex items-center justify-center gap-2 hover:bg-gray-50"><LogOut size={16} />ログアウト</button>
      </div>

      {isEditModalOpen && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsEditModalOpen(false)} />
            <div className="relative bg-white rounded-t-2xl p-6 shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto">
                <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-6"></div>
                <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">{selectedDay}の設定</h3>
                <div className="space-y-6 pb-8">
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase mb-3 block">パターンから選択</label>
                        <div className="space-y-3">
                            {presets.length === 0 && <div className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-lg">登録されたシフトパターンがありません</div>}
                            {presets.map(preset => (
                                <button key={preset.id} onClick={() => setTempSetting({ ...tempSetting, type: 'preset', presetId: preset.id })} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${tempSetting.type === 'preset' && tempSetting.presetId === preset.id ? 'border-blue-500 bg-blue-50' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                                    <div className="flex items-center"><span className={`text-sm font-bold px-2 py-0.5 rounded mr-3 ${preset.color}`}>{preset.label}</span><span className="text-slate-600 font-bold text-sm">{preset.time}</span></div>
                                    {tempSetting.type === 'preset' && tempSetting.presetId === preset.id && <Check size={20} className="text-blue-600" strokeWidth={3} />}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase mb-3 block">時間を指定</label>
                        <div onClick={() => setTempSetting({ ...tempSetting, type: 'custom', startTime: '10:00', endTime: '16:00' })} className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${tempSetting.type === 'custom' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                            <div className="flex items-center justify-between mb-3"><div className="flex items-center font-bold text-slate-700"><Clock size={18} className="mr-2 text-slate-400" /><span>時間指定</span></div>{tempSetting.type === 'custom' && <Check size={20} className="text-blue-600" strokeWidth={3} />}</div>
                            <div className="flex items-center space-x-2"><input type="time" value={tempSetting.startTime || ''} onChange={(e) => setTempSetting({...tempSetting, startTime: e.target.value})} className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 bg-white" disabled={tempSetting.type !== 'custom'} /><span className="text-slate-400">~</span><input type="time" value={tempSetting.endTime || ''} onChange={(e) => setTempSetting({...tempSetting, endTime: e.target.value})} className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 bg-white" disabled={tempSetting.type !== 'custom'} /></div>
                        </div>
                    </div>
                    <button onClick={() => setTempSetting({ ...tempSetting, type: 'off' })} className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${tempSetting.type === 'off' ? 'border-blue-500 bg-blue-50' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                        <div className="flex items-center"><X size={20} className="mr-3 text-slate-400" /><span className="font-bold text-slate-600">希望なし（休み）</span></div>
                        {tempSetting.type === 'off' && <Check size={20} className="text-blue-600" strokeWidth={3} />}
                    </button>
                    <button onClick={handleSave} className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl shadow-lg hover:bg-blue-700 active:scale-95 transition-all mt-6">設定を保存</button>
                </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default UserDefaultSettingsPage;
